<template>
  <div>
    <!-- Backup Codes Display Step -->
    <div v-if="step === 'display'">
      <p class="text-center text--secondary mb-6" style="font-size: 15px; line-height: 1.6;">
        {{ $t('2fa.method.backup_codes.register.subtitle') }}
      </p>

      <div v-if="instructionsData?.codes?.length > 0" class="mb-5 pa-4">
        <v-row dense>
          <v-col
              v-for="(code, index) in instructionsData.codes"
              :key="index"
              cols="6"
              class="pa-1"
          >
            <div class="text-center py-2 " style="background-color: rgba(238,239,239,0.57); border-radius: 5px;">
              <div class="text-body-1 font-weight-medium"
                   style="font-family: 'Courier New', monospace; letter-spacing: 1px; color: #555555">
                {{ code }}
              </div>
            </div>
          </v-col>
        </v-row>
      </div>

      <div class="d-flex justify-center mb-5" style="gap: 12px;">
        <v-btn
            outlined
            color="primary"
            @click="copyCodes"
            class="text-none"
        >
          <v-icon left small>{{ copied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
          {{ $t('2fa.method.backup_codes.copy_codes') }}
        </v-btn>

        <v-btn
            outlined
            color="primary"
            @click="printCodes"
            class="text-none"
        >
          <v-icon left small>mdi-printer</v-icon>
          {{ $t('2fa.method.backup_codes.print_codes') }}
        </v-btn>
      </div>

      <v-btn
          color="primary"
          depressed
          block
          large
          @click="proceedToVerify"
          :disabled="!instructionsData?.codes?.length"
          class="text-none mb-4"
          style="font-weight: 500; letter-spacing: 0;"
      >
        {{ $t('2fa.method.backup_codes.i_saved_them') }}
      </v-btn>

      <v-divider class="my-4" v-if="canCancel"></v-divider>

      <div class="d-flex justify-center" v-if="canCancel">
        <v-btn
            text
            small
            @click="$emit('cancel')"
            class="text-none"
            color="grey darken-1"
        >
          {{ $t('buttons.cancel') }}
        </v-btn>
      </div>
    </div>

    <!-- Backup Codes Verification Step -->
    <div v-if="step === 'verify'">
      <h2 class="text-h5 font-weight-bold text-center mb-3">
        {{ $t('2fa.method.backup_codes.verify.title') }}
      </h2>

      <p class="text-center text--secondary mb-8" style="font-size: 15px; line-height: 1.6;">
        {{ $t('2fa.method.backup_codes.verify.subtitle') }}
      </p>

      <v-alert v-if="error" type="error" dense text dismissible @input="error = null" class="mb-4">
        {{ error }}
      </v-alert>

      <v-text-field
          v-model="code"
          :label="$t('2fa.method.backup_codes.verify.code_label')"
          outlined
          dense
          hide-details="auto"
          :maxlength="codeLength"
          :counter="codeLength"
          :rules="[rules.required, rules.alphanumeric, rules.length(codeLength)]"
          :disabled="twoFactorAuth.verificationLoading"
          :loading="twoFactorAuth.verificationLoading"
          @keydown.enter="verify"
          autofocus
          autocomplete="off"
          class="mb-5 text-uppercase"
      ></v-text-field>

      <v-btn
          color="primary"
          depressed
          block
          large
          @click="verify"
          :disabled="!isCodeValid"
          :loading="twoFactorAuth.verificationLoading"
          class="text-none mb-4"
          style="font-weight: 500; letter-spacing: 0;"
      >
        {{ $t('2fa.method.backup_codes.verify.verify_button') }}
      </v-btn>

      <v-divider class="my-4"></v-divider>

      <div class="d-flex justify-center align-center" style="gap: 12px;">
        <v-btn
            text
            small
            @click="backToDisplay"
            :disabled="twoFactorAuth.verificationLoading"
            class="text-none"
            color="grey darken-1"
        >
          <v-icon small left>mdi-arrow-left</v-icon>
          {{ $t('buttons.back') }}
        </v-btn>

        <v-btn
            v-if="canCancel"
            text
            small
            @click="$emit('cancel')"
            :disabled="twoFactorAuth.verificationLoading"
            class="text-none"
            color="grey darken-1"
        >
          {{ $t('buttons.cancel') }}
        </v-btn>
      </div>
    </div>

    <!-- Hidden print template -->
    <div v-show="false">
      <div id="backup-codes-print" class="pa-5">
        <h2>{{ $t('2fa.method.backup_codes.register.title') }}</h2>
        <p><strong>{{ $t('2fa.method.backup_codes.print.generated_date') }}:</strong> {{ currentDate }}</p>
        <p><strong>{{ $t('2fa.method.backup_codes.print.account') }}:</strong>
          {{ profile.base_data.email || 'Your Account' }}</p>
        <br>
        <div v-for="(code, index) in instructionsData?.codes" :key="index"
             style="font-family: monospace; font-size: 16px; margin: 8px 0;">
          {{ code }}
        </div>
        <br>
        <p><small>{{ $t('2fa.method.backup_codes.print.warning') }}</small></p>
      </div>
    </div>
  </div>
</template>

<script>
import {twoFactorAuthStore} from "@/store/pinia/twoFactorAuthStore";
import {profileStore} from "@/store/pinia/profileStore";

export default {
  name: 'BackupCodesMethodInstructions',
  props: {
    instructionsData: {
      type: Object,
      required: false,
      default: null
    },
    canCancel: {
      type: Boolean,
      default: true,
    },
  },
  setup() {
    const twoFactorAuth = twoFactorAuthStore();
    const profile = profileStore();
    return {twoFactorAuth, profile};
  },
  data() {
    return {
      step: 'display',
      code: '',
      error: null,
      copied: false,
      rules: {
        required: value => !!value || this.$t('validation.required'),
        alphanumeric: value => /^[a-zA-Z0-9]+$/.test(value) || this.$t('validation.alphanumeric'),
        length: len => value => (value && value.length === len) || this.$t('validation.code_length', {length: len}),
      },
    };
  },

  mounted() {

    this.$emit('title-change', this.$t('2fa.method.backup_codes.register.title'))


  },
  computed: {
    codeLength() {
      return 8;
    },
    isCodeValid() {
      return /^[a-zA-Z0-9]{8}$/.test(this.code);
    },
    currentDate() {
      return new Date().toLocaleDateString();
    }
  },
  methods: {
    proceedToVerify() {
      this.step = 'verify';
      this.error = null;
      this.code = '';
    },

    backToDisplay() {
      this.step = 'display';
      this.error = null;
      this.code = '';
    },

    async verify() {
      if (!this.isCodeValid) return;
      this.error = null;

      try {
        const payload = await this.twoFactorAuth.checkRegister2FAMethodCode(
            this.code,
            this.instructionsData?.main_method?.slug
        );

        if (payload.data.payload) {
          this.$emit('completed', payload);
          this.code = '';
          return;
        }
        this.error = this.$t('2fa.method.backup_codes.verify.error.verification_failed');

      } catch (e) {
        this.error = e.response?.data?.message || this.$t('2fa.method.backup_codes.verify.error.verification_error');
      }
    },

    async copyCodes() {
      if (!this.instructionsData?.codes?.length) return;

      const content = this.formatCodesForExport();

      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(content);
        } else {
          const textArea = document.createElement('textarea');
          textArea.value = content;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand('copy');
          textArea.remove();
        }

        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 3000);
      } catch (err) {
        console.error('Failed to copy codes:', err);
        this.error = this.$t('2fa.method.backup_codes.error.copy_failed');
      }
    },

    printCodes() {
      if (!this.instructionsData?.codes?.length) return;

      const printContent = document.getElementById('backup-codes-print');
      const windowPrint = window.open('', '', 'width=800,height=600');

      windowPrint.document.write('<html><head><title>' + this.$t('2fa.method.backup_codes.register.title') + '</title>');
      windowPrint.document.write('<style>body { font-family: Arial, sans-serif; padding: 20px; }</style>');
      windowPrint.document.write('</head><body>');
      windowPrint.document.write(printContent.innerHTML);
      windowPrint.document.write('</body></html>');

      windowPrint.document.close();
      windowPrint.focus();

      setTimeout(() => {
        windowPrint.print();
        windowPrint.close();
      }, 250);
    },

    formatCodesForExport() {
      const codes = this.instructionsData.codes
          .map((code) => `${code}`)
          .join('\n');

      return `${codes}`;
    }
  },
};
</script>

<style lang="scss" scoped>
::v-deep .v-text-field--outlined fieldset {
  border-color: rgba(0, 0, 0, 0.12);
}

::v-deep .v-counter {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
}

::v-deep .text-uppercase input {
  text-transform: uppercase;
}
</style>
